import { useState } from 'react';
import { Box, Button, Collapse, Divider, Paper, Typography } from '@mui/material';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import type { FilterPanelProps } from '../tableTemplate/TableTemplateTypes';
import type { FilterSection } from './filterPanel.types';
import { FilterField, SectionLabel } from '../../filters/FilterField';
import { useFilterOperators } from '../../filters/useFilterOperators';

/** Panel field grid — four equal columns. Single source so every grid stays in sync. */
const FILTER_GRID_COLUMNS = 'repeat(4, 1fr)';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface FilterPanelExtraProps<T> {
  titleKey: string;
  /**
   * Fields rendered as a fixed top row before any sections.
   * When omitted (or empty), sections are rendered directly with no toggle.
   */
  quickFields?: (keyof T)[];
  sections?: FilterSection<T>[];
  showAdvancedLabelKey?: string;
  hideAdvancedLabelKey?: string;
  /** Override sx applied to the outer Paper — use to strip border/radius when embedded inside another surface. */
  paperSx?: SxProps<Theme>;
  /** CSS grid-template-columns for each field grid. Defaults to four equal columns; pass `'1fr'` in a drawer. */
  gridColumns?: string;
  /** Hide the internal search-title header (e.g. when embedded in a drawer that has its own header). */
  hideHeader?: boolean;
}

export type FilterPanelComponentProps<T> = FilterPanelProps<T> & FilterPanelExtraProps<T>;

// ── Generic FilterPanel ────────────────────────────────────────────────────────

export function FilterPanel<T>({
  allColumns,
  filterState,
  onFilterChange,
  onFilterClear,
  titleKey,
  quickFields = [],
  sections,
  showAdvancedLabelKey = 'table.filter.showAdvanced',
  hideAdvancedLabelKey = 'table.filter.hideAdvanced',
  paperSx,
  gridColumns = FILTER_GRID_COLUMNS,
  hideHeader = false,
}: FilterPanelComponentProps<T>) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const hasQuickFields = quickFields.length > 0;
  const hasSections = (sections?.length ?? 0) > 0;

  const { getFilterOp, setFilterOp, getListOp, setListOp } = useFilterOperators(
    filterState,
    onFilterChange,
  );

  const getOptionLabel = (key: keyof T) => {
    const col = allColumns.find(c => c.key === key);
    if (col?.filter?.kind === 'list' && col.filter.optionLabelPrefix) {
      const prefix = col.filter.optionLabelPrefix;
      return (opt: string) => t(`${prefix}.${opt}`, opt);
    }
    return undefined;
  };

  // ── Render a single field by its key ────────────────────────────────────────
  const renderField = (key: keyof T) => {
    const col = allColumns.find(c => c.key === key);
    if (!col?.filter) return null;

    return (
      <FilterField
        key={String(key)}
        def={col.filter}
        value={filterState[key]}
        onChange={v => onFilterChange(key, v)}
        onClear={() => onFilterClear(key)}
        variant="panel"
        label={t(col.labelKey)}
        op={getFilterOp(key)}
        onOpChange={op => setFilterOp(key, op)}
        listOp={getListOp(key)}
        onListOpChange={op => setListOp(key, op)}
        getOptionLabel={getOptionLabel(key)}
      />
    );
  };

  const renderFieldGrid = (fields: (keyof T)[], marginBottom = 2) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 2, mb: marginBottom }}>
      {fields.map(key => renderField(key))}
    </Box>
  );

  const renderSections = () => (
    <>
      {sections!.map((section, i) => (
        <Box key={section.titleKey}>
          <SectionLabel icon={section.icon} label={t(section.titleKey)} />
          {renderFieldGrid(section.fields, i < sections!.length - 1 ? 2.5 : 0)}
        </Box>
      ))}
    </>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        // Theme-drive the shared `--fp-*` custom properties (defaults live in
        // filterPanel.css as portal-safe fallbacks); no hardcoded colours here.
        '--fp-focus-color': theme.palette.primary.main,
        // text.primary is white in this theme (for coloured surfaces); use the
        // dark body colour so field text/labels are readable on light surfaces.
        '--fp-color': theme.palette.text.body,
        '--fp-label-color': theme.palette.text.label,
        '--fp-border-color': theme.palette.divider,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.borderRadius.lg,
        p: 2.5,
        mb: 1.5,
        ...paperSx,
      }}
    >
      {/* ── Header (hidden when embedded in a drawer that has its own) ── */}
      {!hideHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SearchIcon
            sx={{ color: 'primary.main', fontSize: theme.typography.subtitle1.fontSize, mr: 1 }}
          />
          <Typography variant="subtitle1" fontWeight={theme.typography.fontWeightBold} color="primary.main">
            {t(titleKey)}
          </Typography>
        </Box>
      )}

      {/* ── Quick fields — only when provided ── */}
      {hasQuickFields && (
        <>
          {renderFieldGrid(quickFields.slice(0, 4))}
          {quickFields.length > 4 && renderFieldGrid(quickFields.slice(4, 8))}
        </>
      )}

      {/* ── Sections ── */}
      {hasSections &&
        (hasQuickFields ? (
          // Quick fields exist → sections are behind an expand toggle
          <>
            <Button
              size="small"
              color="primary"
              onClick={() => setShowAdvanced(v => !v)}
              endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none', fontWeight: theme.typography.fontWeightSemiBold, fontSize: '0.85rem', mb: 1 }}
            >
              {showAdvanced ? t(hideAdvancedLabelKey) : t(showAdvancedLabelKey)}
            </Button>
            <Collapse in={showAdvanced}>
              <Divider sx={{ mb: 2 }} />
              {renderSections()}
            </Collapse>
          </>
        ) : (
          // No quick fields → sections are the primary content, shown directly
          renderSections()
        ))}
    </Paper>
  );
}
